import React, { useState, useEffect } from 'react';
import { Form, Button, Steps, Row, Col, Card, Alert } from 'antd';
import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import FormField from './FormField';
import { createValidationSchema } from '@/modules/action-flow/shared/utils/form-utils';
import { DataRenderConfig } from "@/modules/action-flow/config/types/dynamic-form-types";
import { StepConfig } from "@/modules/action-flow/config";
import ConfirmApprovalModal from "@/shared/components/ConfirmApprovalModal";

const { Step } = Steps;

interface DynamicWorkflowFormProps {
    config: DataRenderConfig;
    initialData: any;
    onSubmit: (values: any) => void;
    customComponents: Record<string, React.ComponentType<any>>;
    hideSubmissionStep?: boolean;
}

// Helper function to get human-readable action label
const getActionLabel = (actionValue: string, submissionValues: any): string => {
    if (!actionValue) return '';
    
    // Try to find the action label from approval actions if available
    const approvalActions = submissionValues?.approvalActions || [];
    const matchingAction = approvalActions.find((action: any) => 
        action.value === actionValue || action.id === actionValue
    );
    
    if (matchingAction) {
        return matchingAction.label || matchingAction.name || actionValue;
    }
    
    // Fallback: try to make the action value more readable
    if (actionValue.length > 20) {
        // If it looks like a UUID or hash, show a shortened version with ellipsis
        return `${actionValue.substring(0, 8)}...${actionValue.substring(actionValue.length - 8)}`;
    }
    
    // For shorter values, try to format them nicely
    return actionValue.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

const DynamicWorkflowForm: React.FC<DynamicWorkflowFormProps> = ({
                                                                     config,
                                                                     initialData,
                                                                     onSubmit,
                                                                     customComponents,
                                                                     hideSubmissionStep = false
                                                                 }) => {
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionValues, setSubmissionValues] = useState<any>(null);

    // Get hideSubmissionStep from location state if provided
    const locationState = location.state as { hideSubmissionStep?: boolean } | null;
    const shouldHideSubmissionStep = locationState?.hideSubmissionStep || hideSubmissionStep;

    // Filter out the last step (submission step) if hideSubmissionStep is true
    const displaySteps = shouldHideSubmissionStep && config.steps.length > 1
        ? config.steps.slice(0, -1)
        : config.steps;

    const handleConfirmSubmit = (values: any) => {
        setSubmissionValues(values);
        setIsModalVisible(true);
    };

    const handleModalConfirm = async () => {
        try {
            setIsSubmitting(true);
            await onSubmit(submissionValues);
        } finally {
            setIsSubmitting(false);
            setIsModalVisible(false);
        }
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setSubmissionValues(null);
    };

    const renderStepContent = (step: StepConfig, formikProps: any) => {
        const columns = step.layout?.columns || 2;
        const gutter = 16;

        // @ts-ignore
        return (
            <Row gutter={[gutter, gutter]}>
                {step.fields?.map((field) => (
                    <Col
                        key={field.key}
                        span={24 / columns}
                        style={{ marginBottom: '16px' }}
                    >
                        <FormField
                            // @ts-ignore
                            field={field} formikProps={formikProps} />
                    </Col>
                ))}
                {step.customComponents?.map((component, index) => (
                    <Col key={index} span={24}>
                        {renderCustomComponent(component, formikProps)}
                    </Col>
                ))}
            </Row>
        );
    };

    const renderCustomComponent = (component: any, formikProps: any) => {
        const CustomComponent = customComponents[component.componentName];
        if (!CustomComponent) {
            console.warn(`Custom component ${component.componentName} not found`);
            return null;
        }
        return (
            <CustomComponent
                formikProps={formikProps}
                field={component}
                data={formikProps.values}
            />
        );
    };

    // Show read-only message if submission step is hidden
    const renderReadOnlyMessage = () => {
        if (shouldHideSubmissionStep) {
            return (
                <Alert
                    message="View-Only Mode"
                    description="This request has already been processed. You are viewing it in read-only mode."
                    type="info"
                    showIcon
                    className="mb-4"
                />
            );
        }
        return null;
    };

    return (
        <Card className="w-full">
            {renderReadOnlyMessage()}

            <Formik
                initialValues={initialData}
                validationSchema={createValidationSchema(config)}
                onSubmit={handleConfirmSubmit}
            >
                {(formikProps) => (
                    <Form layout="vertical">
                        <Steps current={currentStep} className="mb-8">
                            {displaySteps.map((step, index) => (
                                <Step key={index} title={step.title} />
                            ))}
                        </Steps>

                        <div className="mt-8">
                            {renderStepContent(displaySteps[currentStep], formikProps)}
                        </div>

                        <div className="mt-4 flex justify-between">
                            <Button
                                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                                disabled={currentStep === 0}
                            >
                                Previous
                            </Button>

                            {currentStep < displaySteps.length - 1 ? (
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        const currentStepFields = displaySteps[currentStep]?.fields?.map(f => f.key) || [];
                                        const errors = await formikProps.validateForm();
                                        const currentStepErrors = Object.entries(errors)
                                            .filter(([key]) => currentStepFields.includes(key))
                                            .reduce((acc, [key, value]) => ({...acc, [key]: value}), {});

                                        if (Object.keys(currentStepErrors).length === 0) {
                                            setCurrentStep(currentStep + 1);
                                        } else {
                                            const touchedFields = currentStepFields.reduce((acc, key) => ({
                                                ...acc,
                                                [key]: true
                                            }), {});
                                            formikProps.setTouched(touchedFields);
                                        }
                                    }}
                                >
                                    Next
                                </Button>
                            ) : (
                                !shouldHideSubmissionStep && (
                                    <Button
                                        type="primary"
                                        onClick={() => formikProps.handleSubmit()}
                                        disabled={formikProps.isSubmitting || shouldHideSubmissionStep}
                                    >
                                        Submit
                                    </Button>
                                )
                            )}
                        </div>

                        {
                            /*
                            const { values, errors, touched, setFieldValue } = formikProps;

    // Get approval actions from the workflow data
    const approvalActions = values?.approvalActions || [];

                        */}

                        {!shouldHideSubmissionStep && (
                            <ConfirmApprovalModal
                                open={isModalVisible}
                                onCancel={handleModalCancel}
                                onConfirm={handleModalConfirm}
                                loading={isSubmitting}
                                action={submissionValues?.approvalAction || ''}
                                actionLabel={getActionLabel(submissionValues?.approvalAction, submissionValues)}
                                comment={submissionValues?.approvalComment || ''}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

export default DynamicWorkflowForm;